import React from 'react';
import { observer } from 'mobx-react-lite';
import type { NpvrStatusInfo } from '@smart-tv/typings/npvr';

import RecordingStorageSymbol from '@smart-tv/components/icons/recording-storage-symbol';
import Typography from '@smart-tv/components/typography/typography';
import StorageIndicatorWheel from './storage-indicator-wheel/storage-indicator-wheel';
import styles from './npvr.module.scss';
import { rootStore } from './_stores/store-root';
import { clsx } from 'clsx';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

const stringSplitter = (string: string): string => {
  const parts = string.split(' ');
  switch (parts.length) {
    case 4:
      return parts[0] + ' ' + parts[1] + '\n ' + parts[2] + ' ' + parts[3];
    default:
      return string;
  }
};

const getParts = (status: NpvrStatusInfo | null) => [
  {
    iconClassName: styles['npvr-storage__symbol--recorded'],
    label: 'Optaget',
    value: status ? stringSplitter(status.recordedSpaceDisplayName) : ''
  },
  {
    iconClassName: styles['npvr-storage__symbol--scheduled'],
    label: 'Planlagt',
    value: status ? stringSplitter(status.scheduledSpaceDisplayName) : ''
  },
  {
    iconClassName: styles['npvr-storage__symbol--free'],
    label: 'Ledig',
    value: status ? stringSplitter(status.freeSpaceDisplayName) : ''
  }
];

export const NpvrStorage: React.FC = observer(() => {
  const { data } = rootStore.npvrStore.status;
  const status = data ?? null;

  React.useEffect(() => {
    rootStore.npvrStore.fetchStatus().catch((error) => console.error(error));
  }, []);

  const parts = getParts(status);
  const storageNote = status ? status.spaceWarningDisplayMessage : null;

  return (
    <div className={styles['npvr-storage__content']}>
      <StorageIndicatorWheel status={status} />
      <div className={styles['npvr-storage__info']}>
        <div
          className={clsx(
            styles['npvr-storage__box'],
            IS_BROWSER ? styles['npvr-storage__box--browser'] : styles['npvr-storage__box--tv']
          )}
        >
          {parts.map(({ iconClassName, label, value }) => (
            <div key={label} className={styles['npvr-storage__part']}>
              <RecordingStorageSymbol size={64} className={iconClassName} />
              <div className={styles['npvr-storage__spacer']} />
              <Typography
                variant="bodySmall"
                fontWeight="regular"
                color="labelTint"
                textAlign="center"
              >
                {label}:
              </Typography>
              <Typography
                variant="body"
                fontWeight="bold"
                textAlign="center"
                style={{ whiteSpace: 'pre-line' }}
              >
                {value}
              </Typography>
            </div>
          ))}
        </div>
        {storageNote ? (
          <Typography
            variant="body"
            fontWeight="bold"
            color="errorSecondary"
            className={styles['npvr-storage__warning']}
          >
            {storageNote}
          </Typography>
        ) : null}
      </div>
    </div>
  );
});

export default NpvrStorage;
